import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Golf Start");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen bg-cream grid grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 md:p-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden font-serif text-3xl text-forest">Golf Start</Link>
          <div className="eyebrow mt-8 lg:mt-0">Create an account</div>
          <h1 className="mt-3 font-serif text-5xl text-forest leading-[0.95]">Begin.</h1>
          <p className="mt-4 text-forest-muted font-light">Free forever. No credit card to start.</p>

          <button onClick={handleGoogle} data-testid="google-signup-btn" className="mt-10 w-full border border-forest px-6 py-4 flex items-center justify-center gap-3 hover:bg-forest hover:text-cream-light transition-colors">
            <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="currentColor" d="M21.35 11.1h-9.18v2.92h5.27c-.24 1.42-1.73 4.16-5.27 4.16-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.8 0 3.02.77 3.71 1.43l2.53-2.44C16.82 3.95 14.73 3 12.17 3 6.97 3 2.76 7.21 2.76 12.42S6.97 21.83 12.17 21.83c7.02 0 9.35-4.91 9.35-7.43 0-.5-.05-.88-.17-1.3Z"/></svg>
            <span className="font-sans text-xs tracking-[0.2em] uppercase">Continue with Google</span>
          </button>

          <div className="my-8 flex items-center gap-4 text-xs text-forest-muted uppercase tracking-[0.2em]">
            <div className="flex-1 h-px bg-cream-border" />or with email<div className="flex-1 h-px bg-cream-border" />
          </div>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="eyebrow">I am a</label>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {[
                  { k: "student", l: "Student" },
                  { k: "coach", l: "Coach" },
                ].map((r) => {
                  const active = form.role === r.k;
                  return (
                    <button
                      type="button"
                      key={r.k}
                      data-testid={`role-${r.k}`}
                      onClick={() => setForm({ ...form, role: r.k })}
                      className={`border p-4 transition-colors ${active ? "border-forest bg-forest text-cream-light" : "border-cream-border bg-cream-light text-forest hover:border-forest"}`}
                    >
                      <div className="font-serif text-xl">{r.l}</div>
                      <div className={`mt-1 text-[10px] tracking-[0.2em] uppercase ${active ? "text-cream-light/80" : "text-forest-muted"}`}>
                        {r.k === "student" ? "Book lessons" : "Teach lessons"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="eyebrow">Full name</label>
              <input data-testid="signup-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field mt-2" />
            </div>
            <div>
              <label className="eyebrow">Email</label>
              <input data-testid="signup-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field mt-2" />
            </div>
            <div>
              <label className="eyebrow">Password <span className="lowercase tracking-normal text-forest-muted normal-case">(min 6)</span></label>
              <input data-testid="signup-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field mt-2" />
            </div>
            <button data-testid="signup-submit-btn" disabled={loading} className="btn-primary w-full disabled:opacity-50">
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>

          <p className="mt-10 text-sm text-forest-muted">
            Already a member?{" "}
            <Link to="/login" data-testid="signup-to-login" className="text-forest underline decoration-gold underline-offset-4 hover:text-gold">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src="https://images.pexels.com/photos/6256832/pexels-photo-6256832.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Coach" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/60 via-forest/20 to-forest/85" />
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-cream-light">
          <Link to="/" className="font-serif text-3xl">Golf Start</Link>
          <div className="max-w-md">
            <div className="eyebrow text-gold">Join the Roster</div>
            <div className="mt-4 font-serif text-4xl italic leading-[1.1]">
              "The quiet joy of learning a beautiful game, with a coach who respects your time."
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
